import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '@/components/ui/Input';
import Icon from '@/components/AppIcon';
import StickyActionBar from '@/components/ui/StickyActionBar';
import { getRoleReviewSettings, updateRoleReviewSettings } from '../../../../../api/General/businessRoleSettings';

const MIN_MONTHS = 1;
const MAX_MONTHS = 60;

const clampMonths = (value: number): number => Math.min(MAX_MONTHS, Math.max(MIN_MONTHS, Math.round(value)));

const ReviewSettingsTab: React.FC = () => {
  const { t } = useTranslation();
  const [savedMonths, setSavedMonths] = useState<number>(6);
  const [months, setMonths] = useState<number>(6);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const loadSettings = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getRoleReviewSettings();
      if (result?.errors?.length) {
        setError(result.errors[0]?.description || t('settings.roleReview.loadFail'));
      } else {
        setError(null);
        const value = clampMonths(result.payload?.reviewIntervalMonths ?? 6);
        setSavedMonths(value);
        setMonths(value);
      }
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const hasUnsavedChanges = months !== savedMonths;

  const handleReset = () => setMonths(savedMonths);

  const handleSave = async () => {
    const value = clampMonths(months);
    setIsSaving(true);
    try {
      const result = await updateRoleReviewSettings({ reviewIntervalMonths: value });
      if (result?.errors?.length) {
        showToast(result.errors[0]?.description || t('settings.roleReview.saveFail'), 'error');
      } else {
        const saved = clampMonths(result.payload?.reviewIntervalMonths ?? value);
        setSavedMonths(saved);
        setMonths(saved);
        showToast(t('settings.roleReview.saveSuccess'), 'success');
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Preview the date a role reviewed today would next be due, using the pending interval.
  const nextReviewDate = new Date();
  nextReviewDate.setMonth(nextReviewDate.getMonth() + clampMonths(months));
  const nextReviewLabel = nextReviewDate.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-foreground">{t('settings.roleReview.title')}</h3>
        <p className="text-sm text-muted-foreground">{t('settings.roleReview.description')}</p>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="AlertCircle" size={16} className="text-destructive" />
            <span className="text-sm text-destructive">{error}</span>
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-xl p-6 max-w-xl space-y-4">
        <div>
          <label htmlFor="review-interval-months" className="block text-sm font-medium text-foreground mb-2">
            {t('settings.roleReview.intervalLabel')}
          </label>
          <div className="flex items-center gap-3">
            <Input
              id="review-interval-months"
              type="number"
              min={MIN_MONTHS}
              max={MAX_MONTHS}
              value={months}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMonths(Number(e.target.value))}
              onBlur={() => setMonths((prev) => clampMonths(prev))}
              className="w-28"
              data-testid="role-review-interval-input"
            />
            <span className="text-sm text-muted-foreground">{t('settings.roleReview.monthsSuffix')}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {t('settings.roleReview.intervalHelper', { min: MIN_MONTHS, max: MAX_MONTHS })}
          </p>
        </div>

        <div className="flex items-start gap-3 bg-muted rounded-lg p-4">
          <Icon name="CalendarClock" size={18} className="text-primary mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-medium text-foreground">{t('settings.roleReview.nextReviewLabel')}</p>
            <p className="text-sm text-muted-foreground">
              {t('settings.roleReview.nextReviewPreview', { count: clampMonths(months), date: nextReviewLabel })}
            </p>
          </div>
        </div>
      </div>

      <StickyActionBar
        hasUnsavedChanges={hasUnsavedChanges}
        isSaving={isSaving}
        onReset={handleReset}
        onSave={handleSave}
        t={t}
      />

      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-lg shadow-lg text-sm font-medium flex items-center gap-2 transition-all ${
          toast.type === 'success' ? 'bg-success text-success-foreground' : 'bg-destructive text-destructive-foreground'
        }`}>
          <Icon name={toast.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={16} />
          {toast.message}
          <button onClick={() => setToast(null)} className="ms-3 hover:opacity-80">
            <Icon name="X" size={14} />
          </button>
        </div>
      )}
    </div>
  );
};

export default ReviewSettingsTab;
